import React, { useHistory } from 'react-router-dom';

import {
  ButtonNext,
  Card,
  CardBody,
  CardBodyList,
  CardBodyOption,
  CardHeader,
  Container,
  Subtitle
} from './styled';

import TextHelper from '../../../../../core/helpers/TextHelper';

import Tooltip from '@/presentation/reusables/Tooltip';
import routes from '@/presentation/components/Routes/routingMap';
import Spinner from '@/presentation/components/Spinner';
import iconEmptyState from '../../../../../assets/icons/emptyState.svg';
import ModalDisclamer from './components/ModalDisclaimer';
import Modal from '@/presentation/modules/procedures/components/Modal/Modal';
import { SubsectionHeader } from '@/presentation/reusables/SectionHeader';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import CheckedListItems from '@/presentation/reusables/CheckedList';
import { CheckListItemProps } from '@/presentation/reusables/CheckedList/types';

interface IProps {
  disclaimer: {
    openDisclaimer: boolean;
    setOpenDisclaimer: (value: boolean) => void;
  };
  academic: any;
  modality: any;
  academicPlanProgress: any;
  isProcessingAcademicPlanProgress: boolean;
  subsectionHeader: any;
  handlerBack: any;
}

const AcademicInformationView = ({
  academic,
  modality,
  academicPlanProgress,
  isProcessingAcademicPlanProgress,
  disclaimer,
  subsectionHeader,
  handlerBack
}: IProps) => {
  const history = useHistory();
  const { openDisclaimer, setOpenDisclaimer } = disclaimer;
  const messageByGraduated: CheckListItemProps = {
    itemText: (
      <>
        Completaste tu plan de estudio y{' '}
        <b data-testid="graduate-message">egresaste</b> de tu carrera.
      </>
    ),
    isChecked: true
  };

  const handlerNext = () => {
    GoogleAnalytics.writeLog('portal-course-showProgressByCourses', 'course');
    history.push(routes.student.courses.advanceCurriculumDetalle);
  };
  if (isProcessingAcademicPlanProgress) {
    return <Spinner data-testid="loading" height={300} colorText="#161D1F" />;
  }

  if (!academicPlanProgress?.requiredCourses?.total) {
    return (
      <Container data-testid="NoData">
        <SubsectionHeader
          title={subsectionHeader[2].title}
          onClick={handlerBack}
        />
        <div className="empty-data-container">
          <img src={iconEmptyState} alt="" className="image" />
          <div className="text">
            Actualmente no hay datos disponibles para mostrar. Actualiza esta
            página en unos minutos.
          </div>
        </div>
      </Container>
    );
  }
  return (
    <Container>
      <SubsectionHeader
        title={subsectionHeader[2].title}
        onClick={handlerBack}
      />
      {academic?.isGraduated && (
        <CheckedListItems
          items={[messageByGraduated]}
          itemsAlign="start"
          iconSize={24}
        />
      )}
      <Card>
        <CardHeader>
          <span>Mis datos</span>
        </CardHeader>
        <CardBody>
          <CardBodyList>
            <div>
              <div>
                <span>Carrera:</span>
                <span>{TextHelper.capitalize(academic.progDesc)}</span>
              </div>
              <div>
                <span>Campus:</span>
                <span>
                  {academic.campusDesc === 'CAMPUS VIRTUAL'
                    ? academic?.campusDesc?.replace(/CAMPUS VIRTUAL/gi, 'UTP')
                    : TextHelper.capitalize(academic.campusDesc)}
                </span>
              </div>
            </div>
            <div>
              <div>
                <span>Plan de estudios:</span>
                <span>{academic.plan.toUpperCase()}</span>
              </div>
              <div>
                <span>Modalidad de carrera:</span>
                <span>{TextHelper.capitalize(modality.desc)}</span>
              </div>
            </div>
          </CardBodyList>
        </CardBody>
      </Card>
      <Subtitle>
        <span>Cursos obligatorios</span>
      </Subtitle>
      <Card>
        <CardBody>
          <CardBodyOption className="courses">
            <div>
              <span>{academicPlanProgress.requiredCourses.total}</span>
              <span>Requeridos</span>
            </div>
            <div>
              <span>{academicPlanProgress.requiredCourses.approved}</span>
              <span>Aprobados</span>
            </div>
            <div>
              <span>{academicPlanProgress.requiredCourses.pending}</span>
              <span>Pendientes</span>
            </div>
          </CardBodyOption>
        </CardBody>
      </Card>
      <Subtitle>
        <span>Créditos</span>
        <Tooltip
          width="261px"
          text="Los créditos son una unidad de medida relacionada con la dedicación en horas académicas que tiene un estudiante hacia una determinada asignatura."
          iconSize="14px"
          buttonSize="20px"
          boxToRight
        />
      </Subtitle>
      <p
        style={{
          color: '#161D1F',
          fontSize: 14,
          marginBottom: 4,
          fontWeight: 700
        }}
      >
        Obligatorios
      </p>
      <Card>
        <CardBody>
          <CardBodyOption className="credits">
            <div>
              <span>{academicPlanProgress.credits.required.total}</span>
              <span>Requeridos</span>
            </div>
            <div>
              <span>{academicPlanProgress.credits?.required.approved}</span>
              <span>Aprobados</span>
            </div>
            <div>
              <span>{academicPlanProgress.credits.required.pending}</span>
              <span>Pendientes</span>
            </div>
          </CardBodyOption>
        </CardBody>
      </Card>
      <p
        style={{
          color: '#161D1F',
          fontSize: '14px',
          lineHeight: '20px',
          marginBottom: 4,
          fontWeight: 700
        }}
      >
        Electivos
      </p>
      <Card>
        <CardBody>
          <CardBodyOption className="credits">
            <div>
              <span>{academicPlanProgress.credits.optional.total}</span>
              <span>Requeridos</span>
            </div>
            <div>
              <span>{academicPlanProgress.credits.optional.approved}</span>
              <span>Aprobados</span>
            </div>
            <div>
              <span>{academicPlanProgress.credits.optional.pending}</span>
              <span>Pendientes</span>
            </div>
          </CardBodyOption>
        </CardBody>
      </Card>

      <ButtonNext onClick={() => handlerNext()}>
        <span>Avance por cursos</span>
      </ButtonNext>
      <Modal show={openDisclaimer} handleClose={() => setOpenDisclaimer(false)}>
        <ModalDisclamer handleClick={() => setOpenDisclaimer(false)} />
      </Modal>
    </Container>
  );
};

export default AcademicInformationView;
